import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { useForm, useFieldArray } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { catchAsync, checkResponse } from "../../../../utilities/utilities";
import {
  ADD_QUESTION,
  UPDATE_QUESTION,
  VIEW_QUESTION,
} from "../../../../services/ApiCalls";
import Loader from "../../../../components/Common/Loader";
import { toast } from "react-toastify";

const schema = z.object({
  name: z.string().min(1, { message: "Name is required" }),
  description: z.string().min(1, { message: "Description is required" }),
  questionType: z.enum(["SINGLE", "MULTIPLE"]),
  categoryId: z.string().optional(),
  status: z.enum(["active", "inactive", "archived"]),
  answers: z.array(z.string().min(1, { message: "Answer cannot be empty" })),
});

const AddEditService = () => {
  const [loader, setLoader] = useState(false);
  const [serviceData, setServiceData] = useState();
  const { id, category } = useParams();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    watch,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      description: "",
      questionType: "SINGLE",
      categoryId: category || "",
      status: "active",
      answers: [""],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "answers",
  });

  const questionType = watch("questionType");

  const submitHandler = catchAsync(async (data) => {
    setLoader(true);
    const body = {
      ...data,
      ...(id && { _id: id }),
    };

    console.log(body, "teeqweqwewe");

    const res = await (id ? UPDATE_QUESTION(body) : ADD_QUESTION(body));
    const success = checkResponse({ res, showSuccess: true });

    if (success) {
      navigate(`/manage-service-category/edit/${category}`);
    }
    setLoader(false);
  });

  const getData = catchAsync(async () => {
    setLoader(true);
    const res = await VIEW_QUESTION(id);
    const success = checkResponse({ res, setData: setServiceData, setLoader });
    if (success) {
      const d = res?.data?.data;
      setValue("name", d?.name);
      setValue("description", d?.description);
      setValue("questionType", d?.questionType || "SINGLE");
      setValue("categoryId", d?.categoryId || "");
      setValue("status", d?.status || "active");
      setValue("answers", d?.answers?.length ? d.answers : [""]);
    }
  }, setLoader);

  useEffect(() => {
    if (id) getData();
  }, [id]);

  return (
    <>
      {loader && <Loader />}
      <section className="serviceCategory py-3 position-relative">
        <Container className="py-3">
          <Row className="justify-content-center">
            <Col lg="12">
              <div className="d-flex align-items-center gap-10">
                <Link
                  to={`/manage-service-category/edit/${category}`}
                  className="border d-flex align-items-center p-2 rounded"
                >
                  ←
                </Link>
                <h4 className="mb-0 py-3 fw-bold themeBlue text-capitalize">
                  {id ? "Edit" : "Add"} Question
                </h4>
              </div>
            </Col>

            <Col lg="6" className="my-2">
              <div
                className="cardCstm px-lg-4 px-3 py-2 rounded mt-3"
                style={{ background: "#eee" }}
              >
                <Form className="py-3" onSubmit={handleSubmit(submitHandler)}>
                  <Row>
                    {/* Name */}
                    <Col lg="12" className="my-2">
                      <label className="form-label pb-2 text-muted fw-sbold">
                        Name
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        {...register("name")}
                      />
                      {errors?.name && (
                        <p className="text-danger m-0">
                          {errors?.name?.message}
                        </p>
                      )}
                    </Col>

                    {/* Description */}
                    <Col lg="12" className="my-2">
                      <label className="form-label pb-2 text-muted fw-sbold">
                        Description
                      </label>
                      <textarea
                        rows={4}
                        className="form-control"
                        {...register("description")}
                      />
                      {errors?.description && (
                        <p className="text-danger m-0">
                          {errors?.description?.message}
                        </p>
                      )}
                    </Col>

                    {/* Question Type */}
                    <Col lg="12" className="my-2">
                      <label className="form-label pb-2 text-muted fw-sbold">
                        Question Type
                      </label>
                      <select
                        className="form-control"
                        {...register("questionType")}
                      >
                        <option value="SINGLE">Single</option>
                        <option value="MULTIPLE">Multiple</option>
                      </select>
                    </Col>

                    {/* Answers */}
                    <Col lg="12" className="my-2">
                      <label className="form-label pb-2 text-muted fw-sbold gap-5 me-3">
                        Answers
                      </label>

                      {fields.map((field, index) => (
                        <div key={field.id} className="d-flex gap-2 mb-2">
                          <input
                            type="text"
                            className="form-control"
                            {...register(`answers.${index}`, {
                              required: "Answer is required",
                              validate: (value) =>
                                value.trim() !== "" || "Answer cannot be empty",
                            })}
                          />
                          {fields.length > 1 && (
                            <Button
                              variant="danger"
                              type="button"
                              onClick={() => remove(index)}
                            >
                              X
                            </Button>
                          )}
                        </div>
                      ))}

                      <Button
                        variant="secondary"
                        type="button"
                        onClick={() => append("")}
                      >
                        + Add Answer
                      </Button>

                      {errors?.answers &&
                        typeof errors.answers.message === "string" && (
                          <p className="text-danger m-0">
                            {errors.answers.message}
                          </p>
                        )}

                      {/* Show individual field errors */}
                      {errors?.answers &&
                        errors.answers.map(
                          (err, idx) =>
                            err && (
                              <p key={idx} className="text-danger m-0">
                                {err?.message}
                              </p>
                            )
                        )}
                    </Col>

                    {/* Status */}
                    <Col lg="12" className="my-2">
                      <label className="form-label text-muted fw-sbold">
                        Status
                      </label>
                      <select className="form-control" {...register("status")}>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </Col>

                    {/* Submit */}
                    <Col lg="12" className="my-2">
                      <div className="d-flex justify-content-center gap-10">
                        <Link to={`/manage-service-category/edit/${category}`}>
                          <Button variant="secondary">Back</Button>
                        </Link>
                        <Button type="submit" variant="primary">
                          Submit
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default AddEditService;
